import { createApi } from '@reduxjs/toolkit/query/react';

import { KEEP_UNUSED_DATA_FOR } from '@shared/api/api.constants';
import { fetchBaseQueryWithTokenCheck } from '@shared/utils/api.utils';

import type { FilesFilters } from '../store/investigations.slice';
import type { InquiryState, InsightState } from '../utils/constants';

import { groupInsightsByState, normalizeFile, serializeGetFilesQueryArgs } from './files.api.utils';
import type {
  FilesResponse,
  FilesStatistics,
  GetFilesStatisticsPayload,
  GetInvestigationInsightsPayload,
  Inquiry,
  Insight,
  InsightData,
  InsightsByState,
  Investigation,
  NewInvestigationPayload,
  RawFilesResponse,
} from './files.types';

export interface GetFilesPayload extends Pick<FilesFilters, 'forCurrentUser' | 'filters' | 'timeAmount' | 'timeUnit'> {
  page: number;
  size: number;
  sort?: string | string[];
}

const FILES_ENDPOINT = 'FilesAPI';
const INVESTIGATION_INSIGHTS_ENDPOINT = 'InvestigationInsightsAPI';
const INSIGHT_BY_ID_ENDPOINT = 'InsightByIdAPI';
const TRACE_BY_ID_ENDPOINT = 'TraceByIdAPI';

export const filesApiSlice = createApi({
  reducerPath: 'filesApi',
  baseQuery: fetchBaseQueryWithTokenCheck,
  tagTypes: [FILES_ENDPOINT, INVESTIGATION_INSIGHTS_ENDPOINT, INSIGHT_BY_ID_ENDPOINT, TRACE_BY_ID_ENDPOINT],
  endpoints: (builder) => {
    return {
      getTraceById: builder.query<Investigation, string | undefined>({
        query: (id) => `/api/investigation/v2/${id}`,
        providesTags: [TRACE_BY_ID_ENDPOINT],
      }),
      getFiles: builder.query<FilesResponse, GetFilesPayload>({
        query: ({ page, size, forCurrentUser, filters, timeAmount, timeUnit, sort }) => {
          const params: Record<string, string | boolean | string[]> = {
            page: page.toString(),
            size: size.toString(),
            forCurrentUser,
            timeUnit,
            timeAmount: timeAmount.toString(),
          };

          if (sort) {
            params.sort = sort;
          }

          const body = {
            filters: Object.fromEntries(Object.entries(filters ?? {}).sort(([a], [b]) => a.localeCompare(b))),
          };

          return {
            url: `api/files/v1`,
            method: 'POST',
            params,
            body,
          };
        },
        transformResponse: (response: RawFilesResponse): FilesResponse => ({
          openFiles: {
            content: response.openFiles.content.map(normalizeFile),
            totalElements: response.openFiles.totalElements,
          },
          closedFiles: {
            content: response.closedFiles.content.map(normalizeFile),
            totalElements: response.closedFiles.totalElements,
          },
        }),
        serializeQueryArgs: ({ queryArgs }) => serializeGetFilesQueryArgs(queryArgs),
        keepUnusedDataFor: KEEP_UNUSED_DATA_FOR,
        providesTags: [FILES_ENDPOINT],
      }),
      requestNewTrace: builder.mutation<Investigation, NewInvestigationPayload>({
        query: (body) => ({
          url: '/api/investigation/v2/new',
          method: 'POST',
          body,
        }),
        invalidatesTags: [FILES_ENDPOINT],
      }),
      changeInvestigationStatus: builder.mutation<Investigation, { id: string; isOpen: boolean }>({
        query: ({ id, isOpen }) => ({
          url: `/api/investigation/v2/${id}`,
          method: 'PATCH',
          body: { isOpen },
        }),
        invalidatesTags: [FILES_ENDPOINT, TRACE_BY_ID_ENDPOINT],
      }),
      changeInsightStatus: builder.mutation<Insight, { id: string; state: InsightState }>({
        query: ({ id, state }) => ({
          url: `/api/insight/v1/${id}/state`,
          method: 'PUT',
          body: { state },
        }),
        invalidatesTags: [FILES_ENDPOINT, INVESTIGATION_INSIGHTS_ENDPOINT, INSIGHT_BY_ID_ENDPOINT],
      }),
      changeInquiryStatus: builder.mutation<Inquiry, { id: string; state: InquiryState }>({
        query: ({ id, state }) => ({
          url: `/api/inquiry/v1/${id}/state`,
          method: 'PUT',
          body: { state },
        }),
        invalidatesTags: [FILES_ENDPOINT],
      }),
      getInsightById: builder.query<InsightData, string>({
        query: (insightId) => ({
          url: `/api/insight/v1/${insightId}`,
          method: 'GET',
        }),
        providesTags: [INSIGHT_BY_ID_ENDPOINT],
      }),
      getFilesStatistics: builder.query<FilesStatistics, GetFilesStatisticsPayload>({
        query: ({ forCurrentUser, timeAmount, timeUnit }) => ({
          url: 'api/files/v1/statistics',
          method: 'GET',
          params: {
            forCurrentUser,
            timeAmount: timeAmount.toString(),
            timeUnit,
          },
        }),
        keepUnusedDataFor: KEEP_UNUSED_DATA_FOR,
        providesTags: [FILES_ENDPOINT],
      }),
      getInvestigationInsights: builder.query<InsightsByState, GetInvestigationInsightsPayload>({
        query: ({ investigationId, page = 0, size = 20 }) => ({
          url: `/api/insight/v1/investigation/${investigationId}`,
          method: 'GET',
          params: {
            page: page.toString(),
            size: size.toString(),
          },
        }),
        transformResponse: groupInsightsByState,
        providesTags: [INVESTIGATION_INSIGHTS_ENDPOINT],
      }),
    };
  },
});

export const {
  useGetTraceByIdQuery,
  useLazyGetTraceByIdQuery,
  useRequestNewTraceMutation,
  useChangeInvestigationStatusMutation,
  useChangeInsightStatusMutation,
  useChangeInquiryStatusMutation,
  useGetInsightByIdQuery,
  useLazyGetFilesQuery,
  useGetFilesStatisticsQuery,
  useGetInvestigationInsightsQuery,
} = filesApiSlice;
